/**
 * App reload routing: the App effects that decide where a reloaded
 * tab lands and what it remembers about the open project.
 *
 * Both effects stay in App.tsx at their original positions (the T4 routing
 * decision, DEC-COUNCIL-015: React's effect order must not change). Only their
 * bodies live here, so the reload order can be tested with the real code.
 *
 *   runInitialUrlSync            App's initial URL -> page sync effect
 *   persistQuoteMakerViewReceipt App's QuoteMaker view-state persist effect
 *   readQuoteMakerViewReceipt    App's readQmStateFromStorage (validated read)
 *
 * The receipt is the tab's saved-project record: sessionStorage
 * `qm_last_view_state::<tenant>`, which `handleNavigate` reads to reopen the
 * project after a reload.
 */
import { pageToPath, pathToPage } from '../utils/page-routing';
import { resolveDeniedAppAreaRoute } from '../config/access-areas';

type RouteUser = Parameters<typeof resolveDeniedAppAreaRoute>[1];

export interface InitialUrlSyncOptions {
    user: RouteUser;
    /**
     * App's localTenantSessionVerified: GET /auth/me has confirmed the
     * signed-in business and its local database is open.
     */
    sessionVerified: boolean;
    /** The one-shot marker (App's didInitialUrlSyncRef). */
    syncedRef: { current: boolean };
    currentPage: string;
    navigate: (page: string) => void;
    location?: Pick<Location, 'pathname'>;
    history?: Pick<History, 'replaceState'>;
}

export type InitialUrlSyncResult =
    | 'no-user'
    | 'waiting-for-session'
    | 'already-synced'
    | 'denied'
    | 'navigated'
    | 'stayed';

export function runInitialUrlSync({
    user,
    sessionVerified,
    syncedRef,
    currentPage,
    navigate,
    location = window.location,
    history = window.history,
}: InitialUrlSyncOptions): InitialUrlSyncResult {
    if (!user) return 'no-user';                         // wait for auth
    // Wait for the verified business (RT1, 2026-09-29). `user` is built from
    // the JWT on the reload's first render, before GET /auth/me returns, and
    // until then the tab's saved-project record cannot be read (its key is
    // tenant-scoped). Syncing a reload on /quotemaker that early took the
    // "nothing was open" path: the form was reset, the empty Import screen
    // shown, and the view-state effect then saved it over the record. The
    // one-shot marker is set only when the sync actually runs, and App
    // re-runs this effect when the business is confirmed.
    if (!sessionVerified) return 'waiting-for-session';
    if (syncedRef.current) return 'already-synced';      // already done
    syncedRef.current = true;
    // `/` carries NO page opinion, but pathToPage maps it to 'dashboard'
    // (page-routing.ts: `if (!slug) return 'dashboard'`). Navigating on
    // that answer overrode the role-based landing and dropped an assessor
    // onto the Run Sheet, a list of every quote in the business, which is
    // neither their job nor something they should read. So sync only when
    // the URL NAMES a page. For everyone else this changes nothing: on `/`
    // their landing is already 'dashboard', so the navigation it replaces
    // was a no-op.
    const pathname = String(location.pathname || '/');
    const explicitSlug = pathname.replace(/^\//, '').split(/[/?#]/)[0];
    const urlPage = pathToPage(pathname);
    const deniedRoute = resolveDeniedAppAreaRoute(urlPage, user);
    if (explicitSlug && deniedRoute) {
        const fallback = deniedRoute.fallbackPage;
        history.replaceState({ page: fallback }, '', pageToPath(fallback));
        return 'denied';
    }
    if (explicitSlug && urlPage && urlPage !== currentPage) {
        navigate(urlPage);
        return 'navigated';
    }
    return 'stayed';
}

export interface QuoteMakerViewReceipt {
    stage: number;
    /**
     * Always false since the Stage 1.5 page went (SPEC 2026-10-21 K7). Still
     * written: the record is sessionStorage, so only this tab reads it, and a
     * pre-merge bundle that loads into this tab later (a rollback, which B1
     * makes the plan, or a reload served from the old service-worker cache)
     * refuses a record without the boolean and reopens an empty project (SPEC
     * R3). The reader below already accepts a record without it; stop writing
     * it only once no rollback past the merge is possible.
     */
    summaryViewActive: boolean;
    quoteEditorOpen: boolean;
    importPhase: string;
    currentProjectId: string | null;
    savedAt: number;
}

/** The saved view as read back: every field is optional. */
export interface StoredQuoteMakerView {
    stage?: number;
    summaryViewActive?: boolean;
    quoteEditorOpen?: boolean;
    importPhase?: string;
    currentProjectId?: string | null;
    savedAt?: number;
    [key: string]: unknown;
}

/** A saved view older than this is not replayed. The project still reopens. */
export const QUOTEMAKER_VIEW_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export interface ReadQuoteMakerViewReceiptOptions {
    storage?: Pick<Storage, 'getItem'>;
    now?: number;
}

/**
 * App's readQmStateFromStorage: the validated read of the tab's record, or
 * null when anything is wrong with it.
 *
 * After 48 hours only the project comes back (2026-09-29). The record is
 * rewritten only when the QuoteMaker view changes, so a job left open on
 * Friday afternoon is more than 48 hours old on Monday morning. Reading the
 * whole record as null then meant "nothing was open": a reload (a deploy's
 * service-worker reload, signing back in) opened an empty new project and the
 * view-state effect saved it over the record. The record lives in
 * sessionStorage and dies with the tab, so its project is still the job this
 * tab had open. Only the old view (stage, Summary, the editor, the import
 * phase) is dropped.
 */
export function readQuoteMakerViewReceipt(
    key: string | null,
    { storage = sessionStorage, now = Date.now() }: ReadQuoteMakerViewReceiptOptions = {},
): StoredQuoteMakerView | null {
    try {
        if (!key) return null;
        const raw = storage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        // Validate types - defensive against tampering
        const validStage = typeof parsed.stage === 'number' && Number.isInteger(parsed.stage) && parsed.stage >= 1 && parsed.stage <= 5;
        // The retired Stage 1.5 flag: optional now (K7). A stored true is a
        // record from before the merge and restores Stage 1 (use-app-routing).
        const validSva = parsed.summaryViewActive === undefined || typeof parsed.summaryViewActive === 'boolean';
        const validQeo = typeof parsed.quoteEditorOpen === 'boolean';
        const validIp = parsed.importPhase === undefined || ['import', 'processing', 'form'].includes(parsed.importPhase);
        const validProjectId = parsed.currentProjectId === undefined
            || parsed.currentProjectId === null
            || typeof parsed.currentProjectId === 'string';
        if (!validStage || !validSva || !validQeo || !validIp || !validProjectId) return null;
        // Ignore a stored view older than 48 hours (probably stale), but keep
        // its project (see above).
        if (parsed.savedAt && now - parsed.savedAt > QUOTEMAKER_VIEW_MAX_AGE_MS) {
            const projectId = typeof parsed.currentProjectId === 'string'
                ? parsed.currentProjectId.trim()
                : '';
            return projectId ? { currentProjectId: projectId } : null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export interface PersistQuoteMakerViewReceiptOptions {
    /** App's QM_STATE_KEY: null until the signed-in business is verified. */
    key: string | null;
    currentPage: string;
    view: QuoteMakerViewReceipt;
    /** App's readQmStateFromStorage (validated read of the same key). */
    readStored: () => { currentProjectId?: unknown } | null;
    storage?: Pick<Storage, 'setItem'>;
}

export type PersistQuoteMakerViewReceiptResult = 'skipped' | 'kept' | 'written';

/** QuoteMaker shows the empty Import screen: no project, nothing open. */
export function isEmptyImportView(view: Pick<QuoteMakerViewReceipt,
    'currentProjectId' | 'importPhase' | 'quoteEditorOpen'>): boolean {
    return !view.currentProjectId
        && view.importPhase === 'import'
        && view.quoteEditorOpen !== true;
}

export function persistQuoteMakerViewReceipt({
    key,
    currentPage,
    view,
    readStored,
    storage = sessionStorage,
}: PersistQuoteMakerViewReceiptOptions): PersistQuoteMakerViewReceiptResult {
    if (currentPage !== 'quotemaker') return 'skipped';
    // No verified business, no record: the key is tenant-scoped. This used to
    // write under the literal key "null".
    if (!key) return 'skipped';
    // An empty Import screen never replaces a record that names a project.
    // Every new project the operator asks for (resetForm, CRM prefill,
    // files dropped on the Run Sheet, View project) removes the record
    // first, so the record is still there only when the empty screen was
    // painted without the operator asking: the reload race (RT1), Back into
    // QuoteMaker after a reload, a Quote Run's job before its load paints.
    // Keeping it lets Menu > QuoteMaker and the next reload reopen the job.
    // ("Open in QuoteMaker" opens an existing project and leaves the record
    // alone until that project's own view replaces it.)
    try {
        const stored = readStored();
        const storedProjectId = typeof stored?.currentProjectId === 'string'
            ? stored.currentProjectId.trim()
            : '';
        if (storedProjectId && isEmptyImportView(view)) return 'kept';
    } catch {
        // An unreadable record is not a project to protect.
    }
    try {
        storage.setItem(key, JSON.stringify(view));
    } catch {
        // sessionStorage may be disabled or full - silently ignore
        return 'skipped';
    }
    return 'written';
}
